import React, { useState, useRef, useEffect, useContext } from 'react';
import { Fragment } from "react";
import { Dialog, Transition } from '@headlessui/react';
import { AiOutlineSetting } from 'react-icons/ai';
import { FaRobot } from 'react-icons/fa';
import { AppStateContext } from '../../state/AppProvider';
import { AssistantCreator } from './AssistantCreator/AssistantCreator';
import { AssistantManager } from './AssistantManager/AssistantManager';

interface AssistantDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    defaultTab: "manage" | "create";
    onNewClick: () => void;
    setOnNewClick: React.Dispatch<React.SetStateAction<() => void>>;
}

export const AssistantDialog = ({ open, setOpen, defaultTab, onNewClick, setOnNewClick }: AssistantDialogProps) => {
    const [activeTab, setActiveTab] = useState<"manage" | "create">(defaultTab);
    const cancelButtonRef = useRef(null);
    const canCreateAssistants = useContext(AppStateContext)?.state.canCreateAssistants ?? false;

    useEffect(() => {
        if (open) {
            setActiveTab(canCreateAssistants && defaultTab === "create" ? "create" : "manage");
        }
    }, [open, defaultTab, canCreateAssistants]);

    const closeDialog = () => {
        setOpen(false);
    };

    const tabsData = [
        {
            label: "Manage Assistants",
            value: "manage" as "manage",
            icon: AiOutlineSetting,
            content: (
                <AssistantManager
                    onCancel={closeDialog}
                    onNewClick={onNewClick}
                    setOnNewClick={setOnNewClick}
                />
            ),
        },
        {
            label: "Create Custom Assistant",
            value: "create" as "create",
            icon: FaRobot,
            content: (
                <AssistantCreator
                    onSubmit={closeDialog}
                    onCancel={closeDialog}
                />
            ),
        },
    ].filter(tab => tab.value !== "create" || canCreateAssistants);

    return (
        <Transition show={open} as={Fragment}>
            <Dialog
                className="relative z-[100]"
                initialFocus={cancelButtonRef}
                onClose={() => setOpen(false)}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-jtc-very-dark-grey bg-opacity-50 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="w-full sm:max-w-7xl h-[90vh] flex flex-col transform overflow-hidden rounded-xl bg-jtc-white text-left align-middle shadow-2xl transition-all">
                                <div className="flex-1 flex flex-col border border-jtc-grey rounded-xl overflow-hidden">
                                    {/* Mobile Dropdown */}
                                    <div className="sm:hidden relative">
                                        <select
                                            value={activeTab}
                                            onChange={(e) => setActiveTab(e.target.value as "create" | "manage")}
                                            className="block appearance-none w-full bg-jtc-white border border-jtc-grey text-jtc-black text-lg rounded-lg py-2 pl-3 pr-10 focus:outline-none focus:ring-2 focus:ring-jtc-blue focus:border-jtc-blue dark:bg-jtc-very-dark-grey dark:border-jtc-very-dark-grey dark:text-white"
                                        >
                                            {tabsData.map(({ label, value }) => (
                                                <option key={value} value={value}>
                                                    {label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                            <svg
                                                className="h-5 w-5 text-gray-500"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.586l3.71-4.354a.75.75 0 111.14.976l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </div>
                                    </div>

                                    {/* Desktop Tabs */}
                                    <ul className="hidden text-sm font-medium text-center text-jtc-disabled rounded-lg sm:flex">
                                        {tabsData.map(({ label, value, icon: Icon }) => (
                                            <li key={value} className="w-full focus-within:z-10">
                                                <button
                                                    onClick={() => setActiveTab(value)}
                                                    className={`inline-block w-full p-4 border-r border-gray-200 focus:outline-none ${activeTab === value
                                                        ? 'bg-white text-gray-900'
                                                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                                        }`}
                                                >
                                                    <div className="flex items-center justify-center gap-2 text-lg">
                                                        <Icon className="w-6 h-6" />
                                                        {label}
                                                    </div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* Tab Content */}
                                    <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden p-4">
                                        {tabsData.find(tab => tab.value === activeTab)?.content}
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
